import React, {useState} from 'react';
import {ReactComponent as CloudIcon} from '../../assets/icons/Cloud.svg';
import {useAuth} from '../../context/AuthContext';
import apiClient from '../../context/apiClient';

const FileUploadCard = ({
                            uploadUrl,
                            onSuccess = () => {
                            },
                            label = "Datei hochladen"
                        }) => {
    const {user} = useAuth();
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [errors, setErrors] = useState(null);

    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        setFile(selected);
        setErrors(null);
        setProgress(0);
    };

    const handleClearFile = () => {
        setFile(null);
        setProgress(0);
        setErrors(null);
    };

    const handleUpload = async () => {
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        if (user) formData.append('username', user.identifier);

        try {
            setUploading(true);
            const res = await apiClient.post(uploadUrl, formData, {
                headers: {'Content-Type': 'multipart/form-data'},
                withCredentials: true,
                onUploadProgress: (e) => {
                    const percent = Math.round((e.loaded * 90) / e.total);
                    setProgress(percent);
                }
            });
            setErrors(null);
            onSuccess(res.data);
        } catch (err) {
            const data = err.response?.data;
            if (data && typeof data === 'object' && !Array.isArray(data)) {
                const extractedErrors = Object.entries(data).map(
                    ([field, message]) => `${field}: ${message}`
                );
                setErrors(extractedErrors);
            } else {
                setErrors(Array.isArray(data) ? data : [String(data ?? 'Unbekannter Fehler beim Hochladen.')]);
            }
        } finally {
            setUploading(false);
            setProgress(0);
        }
    };

    return (
        <div className="flex flex-col h-full">
            {/* Scrollable content */}
            <div className="flex flex-col flex-grow overflow-y-auto space-y-4 p-6 min-h-0">

                {/* Dropzone */}
                <div
                    className="border-2 border-dashed border-teal-400 rounded-md p-6 w-full flex flex-col items-center text-center">
                    <CloudIcon className="h-10 w-10 text-teal-500 mb-3"/>
                    <p className="text-gray-700">
                        <span className="font-medium">Datei auswählen: </span>
                        <label className="text-teal-600 underline cursor-pointer">
                            Durchsuchen
                            <input type="file" accept=".xlsx, .xls" className="hidden" onChange={handleFileChange}/>
                        </label>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Nur Excel-Dateien (.xlsx, .xls)</p>
                </div>

                {/* Datei-Vorschau */}
                {file && (
                    <div
                        className="flex items-center justify-between w-full border border-teal-300 rounded px-3 py-2 bg-gray-50">
                        <div className="flex items-center space-x-2">
                            <CloudIcon className="w-5 h-5 text-teal-500"/>
                            <span className="text-sm text-gray-800 truncate max-w-[200px]">{file.name}</span>
                        </div>
                        <button onClick={handleClearFile} className="text-sm text-red-500 hover:underline ml-2">X
                        </button>
                    </div>
                )}

                {/* Fehleranzeige */}
                {errors && errors.length > 0 && (
                    <div
                        className="bg-red-50 border border-red-300 text-red-700 p-4 rounded w-full shadow-sm overflow-y-auto flex-grow">
                        <h4 className="font-semibold mb-2">Fehler beim Upload:</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm pr-2">
                            {errors.map((err, idx) => (
                                <li key={idx}>{err}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Fortschritt */}
                {uploading && (
                    <div className="w-full">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-teal-500 h-2 rounded-full transition-all duration-300"
                                style={{width: `${progress}%`}}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Fixed button */}
            <div className="p-6 bg-white">
                <button
                    onClick={handleUpload}
                    disabled={!file || uploading}
                    className="w-full bg-teal-600 text-white py-2 rounded font-semibold hover:bg-teal-700 transition disabled:opacity-50"
                >
                    {uploading ? 'Wird hochgeladen…' : label}
                </button>
            </div>
        </div>
    );
};

export default FileUploadCard;
